import { useEffect, useState, useCallback, useRef, lazy, Suspense } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../utils/supabase'
import {
	Box,
	CircularProgress,
	Typography,
	useMediaQuery,
	useTheme,
	Collapse,
	Stack
} from '@mui/material'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import 'swiper/css'
import InstagramIconSvg from '../assets/store/Instagram.svg'
import GoogleIconSvg from '../assets/store/google.svg'
import YelpIconSvg from '../assets/store/yelp.svg'
import FacebookIconSvg from '../assets/store/facebook.svg'
import { fetchMerchantGalleryPhotos, getMerchantPhotoUrl, fetchMerchantLogo } from '../utils/merchantPhotoUtils'
import { formatPhoneForDisplay } from '../utils/phoneUtils'
import {
	THEME_COLORS,
	dayLabels,
	formatTime,
	getCurrentDayIndex,
	getOpenStatus,
	formatPrice
} from '../components/store-page/constants'

// Lazy load dialogs for better initial load performance
const StoreHoursPopup = lazy(() => import('../components/store-page/StoreHoursPopup'))
const PhotoGalleryModal = lazy(() => import('../components/store-page/PhotoGalleryModal'))

// Get the correct weekly hours for display purposes
// Uses weekly_hours_display when inherited_hours is false, otherwise weekly_hours
// Assemble full display address from address fields
function getDisplayAddress(merchant) {
	if (!merchant) return ''
	const parts = []
	if (merchant.address) parts.push(merchant.address)
	if (merchant.address_2) parts.push(merchant.address_2)
	const cityStateZip = [merchant.city, merchant.state].filter(Boolean).join(', ')
	if (cityStateZip && merchant.zip) {
		parts.push(`${cityStateZip} ${merchant.zip}`)
	} else if (cityStateZip) {
		parts.push(cityStateZip)
	} else if (merchant.zip) {
		parts.push(merchant.zip)
	}
	return parts.join(', ')
}

// Check if merchant has any address fields
function hasAddress(merchant) {
	return !!(merchant?.address || merchant?.address_2 || merchant?.city || merchant?.state || merchant?.zip)
}

function getDisplayWeeklyHours(merchant) {
	if (!merchant) return null
	if (merchant.inherited_hours === false && merchant.weekly_hours_display) {
		return merchant.weekly_hours_display
	}
	return merchant.weekly_hours
}

// Service Preview Card Component with expandable details
function ServicePreviewCard({ service, formatPrice, currency }) {
	const [showDetails, setShowDetails] = useState(false)

	return (
		<Box
			sx={{
				bgcolor: '#FFFFFF',
				borderRadius: '12px',
				border: '1px solid #CBD5E0',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden'
			}}
		>
			<Box
				sx={{
					p: '16px 0px 16px 24px',
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					gap: '18px'
				}}
			>
				{/* Service Name and See Details */}
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, }}>
					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 500,
							fontSize: '16px',
							lineHeight: '1.21em',
							color: '#000000',
						}}
					>
						{service.name}
					</Typography>
					{service.description && (
						<Typography
							component="span"
							onClick={(e) => {
								e.stopPropagation()
								setShowDetails(!showDetails)
							}}
							sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 500,
								fontSize: '14px',
								lineHeight: '1.21em',
								letterSpacing: '-0.017em',
								color: THEME_COLORS.accent,
								cursor: 'pointer',
								'&:hover': {
									textDecoration: 'underline'
								}
							}}
						>
							{showDetails ? 'Hide details' : 'See details'}
						</Typography>
					)}
				</Box>

				{/* Price Button */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '10px',
							padding: '6px 14px',
							bgcolor: '#EFEFEF',
							borderRadius: '8px 0px 0px 8px'
						}}
					>
						<Typography
							sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 600,
								fontSize: '14px',
								lineHeight: '1.21em',
								color: '#000000'
							}}
						>
							{formatPrice(service.price, currency)}
						</Typography>
					</Box>
				</Box>
			</Box>

			{/* Description Collapse */}
			<Collapse in={showDetails}>
				<Box sx={{ px: '24px', pb: '16px' }}>
					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: '14px',
							lineHeight: '1.5em',
							color: '#000000'
						}}
					>
						{service.description}
					</Typography>
				</Box>
			</Collapse>
		</Box>
	)
}

// Mobile Tab Selector Component - Section Jump Navigation
function MobileTabSelector({ activeTab, onTabChange }) {
	const tabs = [
		{ id: 'about', label: 'About us' },
		{ id: 'info', label: 'Info' },
		{ id: 'services', label: 'Services' }
	]

	return (
		<Box
			sx={{
				position: 'sticky',
				top: 0,
				zIndex: 1000,
				bgcolor: '#FFFFFF',
				display: 'flex',
				borderBottom: '0.8px solid #CBD5E0'
			}}
		>
			{tabs.map((tab) => (
				<Box
					key={tab.id}
					onClick={() => onTabChange(tab.id)}
					sx={{
						flex: 1,
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						padding: '12px 16px',
						cursor: 'pointer',
						borderBottom: activeTab === tab.id ? `3px solid ${THEME_COLORS.accent}` : '3px solid transparent',
						transition: 'border-color 0.2s ease'
					}}
				>
					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 500,
							fontSize: '16px',
							lineHeight: '1.21em',
							letterSpacing: '-0.016em',
							color: activeTab === tab.id ? '#000000' : 'rgba(0, 0, 0, 0.7)'
						}}
					>
						{tab.label}
					</Typography>
				</Box>
			))}
		</Box>
	)
}

// Store Name Overlay Component - Desktop
function StoreNameOverlayDesktop({ merchant, logo }) {
	const openStatus = getOpenStatus(getDisplayWeeklyHours(merchant), merchant?.timezone)

	return (
		<Box
			sx={{
				position: 'absolute',
				bottom: '32px',
				left: '32px',
				right: '32px',
				display: 'flex',
				alignItems: 'flex-start',
				gap: '33px',
				zIndex: 2
			}}
		>
			{/* Circular Avatar */}
			{logo && (
				<Box
					sx={{
						width: '154px',
						height: '154px',
						borderRadius: '50%',
						overflow: 'hidden',
						flexShrink: 0,
						border: '4px solid white',
						boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
					}}
				>
					<Box
						component="img"
						src={getMerchantPhotoUrl(logo)}
						alt={merchant?.name}
						sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
					/>
				</Box>
			)}

			{/* Store Info */}
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					gap: '12px',
					flex: 1,
					minWidth: 0,
					height: '154px',
				}}
			>
				{/* Store Name */}
				<Typography
					sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 600,
						fontSize: '48px',
						lineHeight: '1em',
						color: '#FFFFFF',
						textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
					}}
				>
					{merchant?.name}
				</Typography>

				{/* Open Status */}
				{openStatus && (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Typography
							sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 800,
								fontSize: '20px',
								lineHeight: '1.21em',
								color: openStatus.isOpen ? '#17D841' : '#E03535',
								textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
							}}
						>
							{openStatus.isOpen ? 'Open' : 'Closed'}
						</Typography>
						{openStatus.isOpen && (
							<Typography
								sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 600,
									fontSize: '20px',
									lineHeight: '1.21em',
									color: '#FFFFFF',
									textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
								}}
							>
								until {openStatus.text.replace('Open until ', '')}
							</Typography>
						)}
						{!openStatus.isOpen && openStatus.nextOpen && (
							<Typography
								sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 600,
									fontSize: '20px',
									lineHeight: '1.21em',
									color: '#FFFFFF',
									textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
								}}
							>
								Opens {openStatus.nextOpen}
							</Typography>
						)}
					</Box>
				)}

				{/* Location */}
				{hasAddress(merchant) && (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<LocationOnOutlinedIcon
							sx={{
								width: '20px',
								height: '20px',
								color: '#FFFFFF',
								filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
							}}
						/>
						<Typography
							sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 400,
								fontSize: '20px',
								lineHeight: '1.21em',
								color: '#CECECE',
							}}
						>
							{getDisplayAddress(merchant)}
						</Typography>
					</Box>
				)}

			</Box>
		</Box>
	)
}

// Store Name Overlay Component - Mobile
function StoreNameOverlayMobile({ merchant, logo, onSeeHoursClick }) {
	const openStatus = getOpenStatus(getDisplayWeeklyHours(merchant), merchant?.timezone)

	return (
		<>
			{/* Circular Avatar - positioned inside the photo gallery */}
			{logo && (
				<Box
					sx={{
						position: 'absolute',
						bottom: '15px',
						left: '10px',
						width: '72px',
						height: '72px',
						borderRadius: '50%',
						overflow: 'hidden',
						flexShrink: 0,
						border: '3px solid white',
						boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
						zIndex: 3
					}}
				>
					<Box
						component="img"
						src={getMerchantPhotoUrl(logo)}
						alt={merchant?.name}
						sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
					/>
				</Box>
			)}

			{/* Store Name - positioned next to avatar */}
			<Box
				sx={{
					position: 'absolute',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'flex-start',
					justifyContent: 'center',
					bottom: '15px',
					left: '90px',
					height: '68px',
					zIndex: 2
				}}
			>
				<Typography
					sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 600,
						fontSize: '20px',
						lineHeight: '1.21em',
						color: '#FFFFFF',
						textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap'
					}}
				>
					{merchant?.name}
				</Typography>

				{/* Open Status with See hours link */}
				{openStatus && (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', mt: '4px', flexWrap: 'wrap' }}>
						<Typography
							sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 800,
								fontSize: '12px',
								lineHeight: '1.21em',
								letterSpacing: '-0.016em',
								color: openStatus.isOpen ? '#17D841' : '#E03535',
								textShadow: '0 1px 4px rgba(0, 0, 0, 0.3)'
							}}
						>
							{openStatus.isOpen ? 'Open' : 'Closed'}
						</Typography>
						{openStatus.isOpen && (
							<Typography
								sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 600,
									fontSize: '12px',
									lineHeight: '1.21em',
									letterSpacing: '-0.016em',
									color: '#FFFFFF',
									textShadow: '0 1px 4px rgba(0, 0, 0, 0.3)'
								}}
							>
								until {openStatus.text.replace('Open until ', '')}
							</Typography>
						)}
						{!openStatus.isOpen && openStatus.nextOpen && (
							<Typography
								sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 600,
									fontSize: '12px',
									lineHeight: '1.21em',
									letterSpacing: '-0.016em',
									color: '#FFFFFF',
									textShadow: '0 1px 4px rgba(0, 0, 0, 0.3)'
								}}
							>
								Opens {openStatus.nextOpen}
							</Typography>
						)}
						<Typography
							component="span"
							onClick={onSeeHoursClick}
							sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 600,
								fontSize: '12px',
								lineHeight: '1.21em',
								letterSpacing: '-0.016em',
								marginLeft: '5px',
								color: '#D683FF',
								textShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
								cursor: 'pointer'
							}}
						>
							See hours
						</Typography>
					</Box>
				)}
			</Box>
		</>
	)
}

// Mobile Photo Slideshow Component with pagination dots
function MobilePhotoSlideshow({ photos, onSeeAllPhotos, renderOverlay }) {
	const [activeIndex, setActiveIndex] = useState(0)

	return (
		<Box
			sx={{
				position: 'relative',
				width: '100%',
				aspectRatio: '16/9',
				overflow: 'visible'
			}}
		>
			<Box
				onClick={onSeeAllPhotos}
				sx={{
					width: '100%',
					height: '100%',
					cursor: 'pointer',
					'& .swiper': {
						width: '100%',
						height: '100%'
					}
				}}
			>
				<Swiper
					modules={[Autoplay]}
					autoplay={{
						delay: 4000,
						disableOnInteraction: false,
						pauseOnMouseEnter: true
					}}
					loop={photos.length > 1}
					onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
					style={{ width: '100%', height: '100%' }}
				>
					{photos.map((photo, index) => {
						const url = getMerchantPhotoUrl(photo)
						return (
							<SwiperSlide key={photo.id || index}>
								<Box
									component="img"
									src={url}
									alt={`Store photo ${index + 1}`}
									sx={{
										width: '100%',
										height: '100%',
										objectFit: 'cover',
										pointerEvents: 'none'
									}}
								/>
							</SwiperSlide>
						)
					})}
				</Swiper>
			</Box>
			{renderOverlay()}
			{/* Pagination dots - inside gallery at bottom center */}
			{photos.length > 1 && (
				<Box
					sx={{
						position: 'absolute',
						bottom: '10px',
						left: '50%',
						transform: 'translateX(-50%)',
						display: 'flex',
						gap: '8px',
						zIndex: 10
					}}
				>
					{photos.map((_, index) => (
						<Box
							key={index}
							sx={{
								width: '6px',
								height: '6px',
								borderRadius: '50%',
								bgcolor: index === activeIndex ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)',
								transition: 'background-color 0.2s ease'
							}}
						/>
					))}
				</Box>
			)}
		</Box>
	)
}

// Photo Gallery Component
function PhotoGallery({ photos, merchant, logo, isMobile, onSeeHoursClick, onSeeAllPhotos }) {
	const totalPhotos = photos?.length || 0
	const showSeeAllButton = totalPhotos > 4

	// Render overlay on photos - different for mobile vs desktop
	const renderOverlay = () => (
		<>
			{/* Gradient overlay for text readability */}
			<Box
				sx={{
					position: 'absolute',
					bottom: 0,
					left: 0,
					right: 0,
					height: isMobile ? '80%' : '70%',
					background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(102,102,102,0) 100%)',
					zIndex: 1,
					pointerEvents: 'none'
				}}
			/>
			{isMobile ? (
				<StoreNameOverlayMobile merchant={merchant} logo={logo} onSeeHoursClick={onSeeHoursClick} />
			) : (
				<StoreNameOverlayDesktop merchant={merchant} logo={logo} />
			)}
		</>
	)

	// Mobile: 16:9 aspect ratio for first photo
	const mobileHeight = '208px'

	if (!photos || photos.length === 0) {
		// No photos - show placeholder with overlay
		return (
			<Box
				sx={{
					position: 'relative',
					width: '100%',
					aspectRatio: isMobile ? '16/9' : '2.5/1',
					minHeight: isMobile ? mobileHeight : '300px',
					borderTopLeftRadius: isMobile ? 0 : '12px',
					borderTopRightRadius: isMobile ? 0 : '12px',
					overflow: 'visible',
					bgcolor: THEME_COLORS.accent
				}}
			>
				{renderOverlay()}
			</Box>
		)
	}

	// Mobile: auto slideshow with all photos
	if (isMobile) {
		return (
			<MobilePhotoSlideshow
				photos={photos}
				onSeeAllPhotos={onSeeAllPhotos}
				renderOverlay={renderOverlay}
			/>
		)
	}

	// Desktop: show up to 4 photos with proper aspect ratios
	// Upload aspect ratios: Photo 1 = 16:9, Photo 2 = 3:2, Photos 3&4 = 4:5
	// Layout calculated to minimize cropping:
	// - Left (Photo 1, 16:9): ~70% width
	// - Right column (~30% width): Photo 2 (3:2) on top ~52%, Photos 3&4 (4:5) bottom ~48%
	// - Container aspect ratio ~2.5:1 to fit all photos properly
	const displayPhotos = photos.slice(0, 4)
	const photoCount = displayPhotos.length

	// Single photo - full width with 16:9 aspect ratio
	if (photoCount === 1) {
		const url = getMerchantPhotoUrl(displayPhotos[0])
		return (
			<Box
				sx={{
					position: 'relative',
					width: '100%',
					aspectRatio: '16/9',
					borderRadius: '12px',
					overflow: 'hidden'
				}}
			>
				<Box
					component="img"
					src={url}
					alt="Store photo"
					sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
				/>
				{renderOverlay()}
			</Box>
		)
	}

	// 2 photos - Photo 1 (16:9) left, Photo 2 (3:2) right
	if (photoCount === 2) {
		return (
			<Box
				sx={{
					position: 'relative',
					display: 'flex',
					gap: '4px',
					width: '100%',
					aspectRatio: '2.5/1',
					borderRadius: '12px',
					overflow: 'hidden'
				}}
			>
				<Box sx={{ flex: '0 0 70%', overflow: 'hidden' }}>
					<Box
						component="img"
						src={getMerchantPhotoUrl(displayPhotos[0])}
						alt="Store photo 1"
						sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
					/>
				</Box>
				<Box sx={{ flex: 1, overflow: 'hidden' }}>
					<Box
						component="img"
						src={getMerchantPhotoUrl(displayPhotos[1])}
						alt="Store photo 2"
						sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
					/>
				</Box>
				{renderOverlay()}
			</Box>
		)
	}

	// 3 photos - Photo 1 left, Photo 2 top-right, Photo 3 bottom-right
	if (photoCount === 3) {
		return (
			<Box
				sx={{
					position: 'relative',
					display: 'flex',
					gap: '4px',
					width: '100%',
					aspectRatio: '2.5/1',
					borderRadius: '12px',
					overflow: 'hidden'
				}}
			>
				<Box sx={{ flex: '0 0 70%', overflow: 'hidden' }}>
					<Box
						component="img"
						src={getMerchantPhotoUrl(displayPhotos[0])}
						alt="Store photo 1"
						sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
					/>
				</Box>
				<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
					{/* Photo 2 (3:2 aspect) - gets ~52% height */}
					<Box sx={{ flex: 52, overflow: 'hidden' }}>
						<Box
							component="img"
							src={getMerchantPhotoUrl(displayPhotos[1])}
							alt="Store photo 2"
							sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
						/>
					</Box>
					{/* Photo 3 (4:5 aspect) - gets ~48% height */}
					<Box sx={{ flex: 48, overflow: 'hidden' }}>
						<Box
							component="img"
							src={getMerchantPhotoUrl(displayPhotos[2])}
							alt="Store photo 3"
							sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
						/>
					</Box>
				</Box>
				{renderOverlay()}
			</Box>
		)
	}

	// 4 photos layout based on upload aspect ratios:
	// Photo 1: 16:9 (1920×1080) - left, full height
	// Photo 2: 3:2 (1200×800) - top right
	// Photos 3&4: 4:5 (800×1000) - bottom right, side by side
	// 
	// Calculated proportions to minimize cropping:
	// - Left photo: ~70% width (Photo 1 at 16:9 fills nicely)
	// - Right column: ~30% width
	// - Right column height split: Photo 2 ~52%, bottom row ~48%
	return (
		<Box
			sx={{
				position: 'relative',
				display: 'flex',
				gap: '4px',
				width: '100%',
				aspectRatio: '2.5/1',
				overflow: 'hidden'
			}}
		>
			{/* Left: Photo 1 (16:9 aspect, ~70% width) */}
			<Box sx={{ flex: '0 0 70%', overflow: 'hidden' }}>
				<Box
					component="img"
					src={getMerchantPhotoUrl(displayPhotos[0])}
					alt="Store photo 1"
					sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
				/>
			</Box>
			{/* Right column (~30% width) */}
			<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
				{/* Top: Photo 2 (3:2 aspect) - ~52% of column height */}
				<Box sx={{ flex: 52, overflow: 'hidden' }}>
					<Box
						component="img"
						src={getMerchantPhotoUrl(displayPhotos[1])}
						alt="Store photo 2"
						sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
					/>
				</Box>
				{/* Bottom row: Photos 3&4 (4:5 aspect each) - ~48% of column height */}
				<Box sx={{ flex: 48, display: 'flex', gap: '4px' }}>
					<Box sx={{ flex: 1, overflow: 'hidden' }}>
						<Box
							component="img"
							src={getMerchantPhotoUrl(displayPhotos[2])}
							alt="Store photo 3"
							sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
						/>
					</Box>
					<Box sx={{ flex: 1, overflow: 'hidden' }}>
						<Box
							component="img"
							src={getMerchantPhotoUrl(displayPhotos[3])}
							alt="Store photo 4"
							sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
						/>
					</Box>
				</Box>
			</Box>
			{renderOverlay()}
			{/* See all photos button - positioned at bottom right of gallery */}
			{showSeeAllButton && (
				<Box
					onClick={onSeeAllPhotos}
					sx={{
						position: 'absolute',
						bottom: '18px',
						right: '18px',
						zIndex: 5,
						display: 'flex',
						alignItems: 'center',
						gap: '6px',
						bgcolor: 'transparent',
						border: '0.5px solid #ffffff',
						borderRadius: '8px',
						px: '16px',
						py: '12px',
						cursor: 'pointer',
						boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
						transition: 'all 0.2s ease',
					}}
				>
					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: '14px',
							lineHeight: '1.21em',
							color: '#ffffff'
						}}
					>
						See all {totalPhotos} photos
					</Typography>
				</Box>
			)}
		</Box>
	)
}

// About Section Component - displays merchant description
function AboutSection({ merchant, isMobile }) {
	if (!merchant?.description) {
		return null
	}

	return (
		<Box sx={{ width: '100%', px: isMobile ? '16px' : '14px', mb: isMobile ? '8px' : 8 }}>
			{/* Section Title with left border accent */}
			<Box
				sx={{
					position: 'relative',
					pl: '20px',
					mb: '16px'
				}}
			>
				{/* Rounded left accent bar */}
				<Box
					sx={{
						position: 'absolute',
						left: 0,
						top: 0,
						bottom: 0,
						width: isMobile ? '5px' : '6px',
						bgcolor: THEME_COLORS.accent,
						borderTopLeftRadius: '8px',
						borderBottomLeftRadius: '8px'
					}}
				/>
				<Typography
					sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 600,
						fontSize: isMobile ? '24px' : '30px',
						lineHeight: '1.21em',
						color: '#000000'
					}}
				>
					About us
				</Typography>
			</Box>

			{/* Description Text - always show full content */}
			<Typography
				sx={{
					fontFamily: 'Inter, sans-serif',
					fontWeight: 400,
					fontSize: isMobile ? '14px' : '18px',
					lineHeight: '1.5em',
					color: 'rgba(0, 0, 0, 0.7)',
					whiteSpace: 'pre-line'
				}}
			>
				{merchant.description}
			</Typography>
		</Box>
	)
}

// Services Preview Component
function ServicesPreview({ services, merchant, isMobile }) {
	const [expandedCategories, setExpandedCategories] = useState({})
	const [activeTab, setActiveTab] = useState('services') // 'services' | 'addons'

	// Filter items based on active tab
	const displayedItems = services.filter(item => {
		if (activeTab === 'services') {
			return item.is_service
		} else {
			return item.is_add_on
		}
	})

	// Group items by category
	const groupedItems = displayedItems.reduce((acc, item) => {
		const categoryName = item.categories?.name || 'Uncategorized'
		if (!acc[categoryName]) {
			acc[categoryName] = []
		}
		acc[categoryName].push(item)
		return acc
	}, {})

	// Build a map of category name -> order from the service data
	const categoryOrderMap = {}
	displayedItems.forEach(item => {
		if (item.categories?.name && item.categories.order != null) {
			categoryOrderMap[item.categories.name] = item.categories.order
		}
	})

	const sortCategoryKeys = (keys) => keys.sort((a, b) => {
		if (a === 'Uncategorized') return 1
		if (b === 'Uncategorized') return -1
		const orderA = categoryOrderMap[a]
		const orderB = categoryOrderMap[b]
		if (orderA != null && orderB != null) return orderA - orderB
		if (orderA != null) return -1
		if (orderB != null) return 1
		return a.localeCompare(b)
	})

	// Initialize expanded state for all categories
	useEffect(() => {
		const categoryKeys = Object.keys(groupedItems)
		if (categoryKeys.length > 0 && Object.keys(expandedCategories).length === 0) {
			const sortedKeys = sortCategoryKeys([...categoryKeys])
			const initialState = {}
			sortedKeys.forEach((category, index) => {
				initialState[category] = index === 0
			})
			setExpandedCategories(initialState)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [displayedItems.length])

	const toggleCategory = (category) => {
		setExpandedCategories(prev => ({
			...prev,
			[category]: !prev[category]
		}))
	}

	// Sort categories by order (nulls last, then alphabetically), Uncategorized always last
	const sortedCategories = sortCategoryKeys(Object.keys(groupedItems))

	// Check if there are add-ons available
	const hasAddOns = services.some(s => s.is_add_on)

	if (displayedItems.length === 0 && !hasAddOns) {
		return null
	}

	return (
		<Box sx={{ width: '100%', px: isMobile ? '16px' : '14px' }}>
			{/* Section Title with left border accent */}
			<Box
				sx={{
					position: 'relative',
					pl: '20px',
					mb: '16px'
				}}
			>
				{/* Rounded left accent bar */}
				<Box
					sx={{
						position: 'absolute',
						left: 0,
						top: 0,
						bottom: 0,
						width: isMobile ? '5px' : '6px',
						bgcolor: THEME_COLORS.accent,
						borderTopLeftRadius: '8px',
						borderBottomLeftRadius: '8px'
					}}
				/>
				<Typography
					sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 600,
						fontSize: isMobile ? '24px' : '30px',
						lineHeight: '1.21em',
						color: '#000000'
					}}
				>
					Services
				</Typography>
			</Box>

			{/* Tabs */}
			{hasAddOns && (
				<Box sx={{ display: 'flex', gap: '10px', mb: '16px' }}>
					<Box
						onClick={() => setActiveTab('services')}
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: activeTab === 'services' ? 600 : 400,
							fontSize: '16px',
							lineHeight: '1.21em',
							letterSpacing: '-0.016em',
							color: activeTab === 'services' ? '#FFFFFF' : '#2D3748',
							bgcolor: activeTab === 'services' ? THEME_COLORS.accent : '#ffffff',
							border: activeTab === 'services' ? 'none' : '1px solid #CBD5E0',
							borderRadius: '1000px',
							padding: isMobile ? '10px 20px' : '12px 26px',
							cursor: 'pointer',
							'&:hover': {
								bgcolor: activeTab === 'services' ? THEME_COLORS.accentHover : THEME_COLORS.primaryLightHover
							}
						}}
					>
						Services
					</Box>
					<Box
						onClick={() => setActiveTab('addons')}
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: activeTab === 'addons' ? 600 : 400,
							fontSize: '16px',
							lineHeight: '1.21em',
							letterSpacing: '-0.016em',
							color: activeTab === 'addons' ? '#FFFFFF' : '#2D3748',
							bgcolor: activeTab === 'addons' ? THEME_COLORS.accent : '#ffffff',
							border: activeTab === 'addons' ? 'none' : '1px solid #CBD5E0',
							borderRadius: '1000px',
							padding: isMobile ? '10px 20px' : '12px 26px',
							cursor: 'pointer',
							'&:hover': {
								bgcolor: activeTab === 'addons' ? THEME_COLORS.accentHover : THEME_COLORS.primaryLightHover
							}
						}}
					>
						Add-ons
					</Box>
				</Box>
			)}

			{/* Services List */}
			{displayedItems.length === 0 ? (
				<Typography color="text.secondary" sx={{ py: 2 }}>
					No {activeTab === 'services' ? 'services' : 'add-ons'} available
				</Typography>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
					{sortedCategories.map((category) => {
						const categoryItems = groupedItems[category]
						const isExpanded = expandedCategories[category] ?? true

						return (
							<Box
								key={category}
								sx={{
									bgcolor: '#FFFFFF',
									borderRadius: '12px',
									border: '1px solid rgba(0, 0, 0, 0.2)',
									overflow: 'hidden'
								}}
							>
								{/* Category Header */}
								<Box
									onClick={() => toggleCategory(category)}
									sx={{
										bgcolor: '#FFFFFF',
										cursor: 'pointer',
										p: isMobile ? '16px 20px' : '16px 24px',
										display: 'flex',
										alignItems: 'center',
										gap: '18px',
										'&:hover': {
											bgcolor: '#F7FAFC'
										}
									}}
								>
									<Typography
										sx={{
											fontFamily: 'Roboto, sans-serif',
											fontWeight: 500,
											fontSize: isMobile ? '18px' : '22px',
											lineHeight: isMobile ? '1.4em' : '1.27em',
											color: '#000000',
											flex: 1
										}}
									>
										{category}
									</Typography>
									{/* Expand/Collapse Icon with purple theme */}
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											height: '40px',
											width: '40px',
											bgcolor: isExpanded ? THEME_COLORS.primaryLight : '#FFFFFF',
											border: `1px solid ${isExpanded ? THEME_COLORS.primary : '#CBD5E0'}`,
											borderRadius: '6px'
										}}
									>
										{isExpanded ? (
											<ExpandLessIcon fontSize='medium' sx={{ color: THEME_COLORS.primary, fontsize: '24px' }} />
										) : (
											<ExpandMoreIcon fontSize='medium' sx={{ color: '#CBD5E0', fontsize: '24px' }} />
										)}
									</Box>
								</Box>

								{/* Category Items */}
								<Collapse in={isExpanded}>
									{/* Border with padding-aligned width */}
									<Box sx={{ px: isMobile ? '20px' : '24px' }}>
										<Box sx={{ width: '100%', height: '1px', borderBottom: '1px solid #CBD5E0' }} />
									</Box>
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
											gap: isMobile ? '8px' : '16px',
											p: isMobile ? '18px 20px' : '22px 24px'
										}}
									>
										{categoryItems.map((service) => (
											<ServicePreviewCard
												key={service.id}
												service={service}
												formatPrice={formatPrice}
												currency={merchant?.currency}
											/>
										))}
									</Box>
								</Collapse>
							</Box>
						)
					})}
				</Box>
			)}
		</Box>
	)
}

// Info Block Row Component - matches Figma exactly
function InfoBlockRow({ label, children, icon }) {
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'stretch',
				gap: '16px',
				width: '100%',
				py: '8px'
			}}
		>
			{/* Text Content */}
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					gap: '2px'
				}}
			>
				{/* Label */}
				<Typography
					sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 500,
						fontSize: '16px',
						lineHeight: '1.21em',
						color: '#000000'
					}}
				>
					{label}
				</Typography>
				{/* Value Content */}
				{children}
			</Box>
			{/* Icon - stretches full height, centered vertically */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					alignSelf: 'stretch'
				}}
			>
				{icon}
			</Box>
		</Box>
	)
}

// Store Info Component
function StoreInfo({ merchant, isMobile, showStickyHeader = false, logo = null, socialLinks = {} }) {
	const [hoursPopupOpen, setHoursPopupOpen] = useState(false)
	const weeklyHours = getDisplayWeeklyHours(merchant)

	// Check if any social links are present
	const hasSocialLinks = socialLinks?.instagram || socialLinks?.google || socialLinks?.yelp || socialLinks?.facebook
	const openStatus = getOpenStatus(weeklyHours, merchant?.timezone)

	// Get today's hours text or next opening info
	// Combines multiple intervals: earliest start, latest end
	const getHoursDisplayText = () => {
		if (!weeklyHours || !merchant?.timezone) return null
		const todayIndex = getCurrentDayIndex(merchant?.timezone)

		// Check today's hours first
		const todayIntervals = weeklyHours[String(todayIndex)] || []
		if (todayIntervals.length > 0) {
			const start = todayIntervals[0].start
			const end = todayIntervals[todayIntervals.length - 1].end
			return `${formatTime(start)} - ${formatTime(end)}`
		}

		// Find next day with hours
		for (let i = 1; i <= 7; i++) {
			const nextDay = (todayIndex + i) % 7
			const intervals = weeklyHours[String(nextDay)] || []
			if (intervals.length > 0) {
				const start = intervals[0].start
				const end = intervals[intervals.length - 1].end
				return `Opens ${formatTime(start)} - ${formatTime(end)} ${dayLabels[nextDay]}`
			}
		}
		return null
	}

	// Mobile version - simpler layout
	if (isMobile) {
		return (
			<Box sx={{ px: '16px' }}>
				{/* Information Title */}

				<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '16px' }}>
					<Box sx={{ position: 'relative', pl: '20px' }}>
						<Box
							sx={{
								position: 'absolute',
								left: 0,
								top: 0,
								bottom: 0,
								width: isMobile ? '5px' : '6px',
								bgcolor: THEME_COLORS.accent,
								borderTopLeftRadius: '8px',
								borderBottomLeftRadius: '8px'
							}}
						/>
						<Typography
							sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 600,
								fontSize: '24px',
								lineHeight: '1em',
								letterSpacing: '0.00625em',
								color: '#000000'
							}}
						>
							Information
						</Typography>
					</Box>
				</Box>
				{/* Phone */}
				{merchant?.phone && (
					<Box sx={{ mb: '4px' }}>
						<InfoBlockRow
							label="Call us"
							icon={<PhoneOutlinedIcon sx={{ width: '20px', height: '20px', color: '#4A5568' }} />}
						>
							<Typography
								component="a"
								href={`tel:${merchant.phone}`}
								sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 400,
									fontSize: '14px',
									lineHeight: '1.71em',
									color: '#2D3748',
									textDecoration: 'none'
								}}
							>
								{formatPhoneForDisplay(merchant.phone)}
							</Typography>
						</InfoBlockRow>
						<Box sx={{ width: '100%', height: '1px', bgcolor: '#CBD5E0', my: '4px' }} />
					</Box>
				)}

				{/* Hours */}
				{weeklyHours && (
					<Box sx={{ mb: '4px' }}>
						<Box
							onClick={() => setHoursPopupOpen(true)}
							sx={{ cursor: 'pointer' }}
						>
							<InfoBlockRow
								label="Hours"
								icon={<ScheduleOutlinedIcon sx={{ width: '18px', height: '18px', color: '#4A5568' }} />}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
									<Typography
										component="span"
										sx={{
											fontFamily: 'Inter, sans-serif',
											fontWeight: 400,
											fontSize: '14px',
											lineHeight: '1.71em',
											color: openStatus?.isOpen ? '#17D841' : '#E03535'
										}}
									>
										{openStatus?.isOpen ? 'Open' : 'Closed'}
									</Typography>
									<Typography
										component="span"
										sx={{
											fontFamily: 'Inter, sans-serif',
											fontWeight: 400,
											fontSize: '14px',
											lineHeight: '1.71em',
											color: '#2D3748'
										}}
									>
										{getHoursDisplayText()}
									</Typography>
								</Box>
							</InfoBlockRow>
						</Box>
						<Box sx={{ width: '100%', height: '1px', bgcolor: '#CBD5E0', my: '4px' }} />
					</Box>
				)}

				{/* Website */}
				{merchant?.website && (
					<Box sx={{ mb: '4px' }}>
						<Box
							onClick={() => window.open(merchant.website.startsWith('http') ? merchant.website : `https://${merchant.website}`, '_blank')}
							sx={{ cursor: 'pointer' }}
						>
							<InfoBlockRow
								label="Website"
								icon={<OpenInNewOutlinedIcon sx={{ width: '20px', height: '20px', color: '#4A5568' }} />}
							>
								<Typography
									sx={{
										fontFamily: 'Inter, sans-serif',
										fontWeight: 400,
										fontSize: '14px',
										lineHeight: '1.71em',
										color: '#2D3748'
									}}
								>
									{merchant.website.replace(/^https?:\/\//, '')}
								</Typography>
							</InfoBlockRow>
						</Box>
						<Box sx={{ width: '100%', height: '1px', bgcolor: '#CBD5E0', my: '4px' }} />
					</Box>
				)}

				{/* Location */}
				{hasAddress(merchant) && (
					<InfoBlockRow
						label="Location"
						icon={<LocationOnOutlinedIcon sx={{ width: '20px', height: '20px', color: '#4A5568' }} />}
					>
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '1.71em', color: '#2D3748' }}>
							{getDisplayAddress(merchant)}
						</Typography>
					</InfoBlockRow>
				)}

				{/* Google Map */}
				{hasAddress(merchant) && (
					<StoreLocationMap address={getDisplayAddress(merchant)} />
				)}

				{/* Hours Popup - Lazy loaded */}
				<Suspense fallback={null}>
					<StoreHoursPopup
						open={hoursPopupOpen}
						onClose={() => setHoursPopupOpen(false)}
						weeklyHours={weeklyHours}
						timezone={merchant?.timezone}
					/>
				</Suspense>
			</Box>
		)
	}

	// Desktop version - matches Figma exactly
	return (
		<Box
			sx={{
				bgcolor: '#FFFFFF',
				borderRadius: '12px',
				p: '16px',
				display: 'flex',
				flexDirection: 'column',
				border: '1px solid rgba(0, 0, 0, 0.1)',
				boxShadow: '0px 4px 4px -4px rgba(12, 12, 13, 0.05), 0px 16px 32px -4px rgba(12, 12, 13, 0.1)',
				height: 'fit-content'
			}}
		>
			{/* Sticky Header - Shows when scrolled past photo gallery with slide animation */}
			<Collapse in={showStickyHeader} timeout={300}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '16px',
						pb: '20px',
						borderBottom: '1px solid #CBD5E0',
						mb: '9px',
						width: '100%'
					}}
				>
					{/* Avatar */}
					{logo && (
						<Box
							sx={{
								width: '80px',
								height: '80px',
								borderRadius: '50%',
								overflow: 'hidden',
								flexShrink: 0,
								border: '3px solid #E2E8F0',
								boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
							}}
						>
							<Box
								component="img"
								src={getMerchantPhotoUrl(logo)}
								alt={merchant?.name}
								sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
							/>
						</Box>
					)}
					{/* Store Name & Status */}
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography
							sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 600,
								fontSize: '24px',
								lineHeight: '1.3em',
								color: '#000000',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap'
							}}
						>
							{merchant?.name}
						</Typography>
						{openStatus && (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '4px' }}>
								<Typography
									sx={{
										fontFamily: 'Inter, sans-serif',
										fontWeight: 800,
										fontSize: '16px',
										lineHeight: '1.4em',
										color: openStatus.isOpen ? '#17D841' : '#E03535'
									}}
								>
									{openStatus.isOpen ? 'Open' : 'Closed'}
								</Typography>
								{openStatus.isOpen && (
									<Typography
										sx={{
											fontFamily: 'Inter, sans-serif',
											fontWeight: 600,
											fontSize: '16px',
											lineHeight: '1.4em',
											color: '#2D3748'
										}}
									>
										until {openStatus.text.replace('Open until ', '')}
									</Typography>
								)}
								{!openStatus.isOpen && openStatus.nextOpen && (
									<Typography
										sx={{
											fontFamily: 'Inter, sans-serif',
											fontWeight: 600,
											fontSize: '16px',
											lineHeight: '1.4em',
											color: '#2D3748'
										}}
									>
										Opens {openStatus.nextOpen}
									</Typography>
								)}
							</Box>
						)}
					</Box>
				</Box>
			</Collapse>

			{/* Store Card Section */}
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '10px', width: '100%' }}>
				{/* Book Now CTA */}
				{merchant?.active && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							gap: '16px',
							py: '10px',
							width: '100%'
						}}
					>
						<Box
							component={Link}
							to={`/store/${merchant?.slug}/book`}
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								width: '100%',
								gap: '10px',
								p: '16px',
								bgcolor: THEME_COLORS.accent,
								border: `1px solid ${THEME_COLORS.accent}`,
								borderRadius: '33px',
								boxShadow: '0px 4px 10px 0px rgba(181, 33, 255, 1)',
								textDecoration: 'none',
								fontFamily: 'Inter, sans-serif',
								fontWeight: 700,
								fontSize: '22px',
								lineHeight: '1.21em',
								letterSpacing: '-0.011em',
								color: '#FFFFFF',
								transition: 'all 0.2s ease',
								'&:hover': {
									bgcolor: THEME_COLORS.accentHover,
									boxShadow: '0px 6px 14px 0px rgba(181, 33, 255, 0.9)'
								}
							}}
						>
							Book now
						</Box>
					</Box>
				)}

				{!merchant?.active && (
					<Box sx={{ py: '10px' }}>
						<Typography
							sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 400,
								fontSize: '14px',
								color: '#718096',
								textAlign: 'center'
							}}
						>
							This store is currently not accepting bookings.
						</Typography>
					</Box>
				)}

				{hasSocialLinks &&
					(<Box sx={{ width: '100%', height: '0px', borderBottom: '1px solid #CBD5E0' }} />)}

				{/* Find us online - Social Links */}
				{hasSocialLinks && (
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', pt: '4px', paddingBottom: '25px' }}>
						<Typography
							sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 500,
								fontSize: '18px',
								lineHeight: '1.71em',
								// 80% black color
								color: 'rgba(0, 0, 0, 0.8)'
							}}
						>
							Find us online
						</Typography>
						<Box sx={{ display: 'flex', gap: '12px' }}>
							{socialLinks?.instagram && (
								<Box
									component="a"
									href={socialLinks.instagram.startsWith('http') ? socialLinks.instagram : `https://${socialLinks.instagram}`}
									target="_blank"
									rel="noopener noreferrer"
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										textDecoration: 'none',
										transition: 'opacity 0.2s ease',
										'&:hover': {
											opacity: 0.7
										}
									}}
								>
									<Box component="img" src={InstagramIconSvg} alt="Instagram" sx={{ width: '44px', height: '44px' }} />
								</Box>
							)}
							{socialLinks?.google && (
								<Box
									component="a"
									href={socialLinks.google.startsWith('http') ? socialLinks.google : `https://${socialLinks.google}`}
									target="_blank"
									rel="noopener noreferrer"
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										textDecoration: 'none',
										transition: 'opacity 0.2s ease',
										'&:hover': {
											opacity: 0.7
										}
									}}
								>
									<Box component="img" src={GoogleIconSvg} alt="Google" sx={{ width: '36px', height: '36px' }} />
								</Box>
							)}
							{socialLinks?.yelp && (
								<Box
									component="a"
									href={socialLinks.yelp.startsWith('http') ? socialLinks.yelp : `https://${socialLinks.yelp}`}
									target="_blank"
									rel="noopener noreferrer"
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										textDecoration: 'none',
										transition: 'opacity 0.2s ease',
										'&:hover': {
											opacity: 0.7
										}
									}}
								>
									<Box component="img" src={YelpIconSvg} alt="Yelp" sx={{ width: '36px', height: '36px' }} />
								</Box>
							)}
							{socialLinks?.facebook && (
								<Box
									component="a"
									href={socialLinks.facebook.startsWith('http') ? socialLinks.facebook : `https://${socialLinks.facebook}`}
									target="_blank"
									rel="noopener noreferrer"
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										textDecoration: 'none',
										transition: 'opacity 0.2s ease',
										'&:hover': {
											opacity: 0.7
										}
									}}
								>
									<Box component="img" src={FacebookIconSvg} alt="Facebook" sx={{ width: '36px', height: '36px' }} />
								</Box>
							)}
						</Box>
					</Box>
				)}
			</Box>

			{/* Separator Line */}
			<Box sx={{ width: '100%', height: '0px', borderBottom: '1px solid #CBD5E0' }} />

			{/* Information Block */}
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '14px', width: '100%', paddingTop: '25px' }}>
				{/* Information Title */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: '24px',
							lineHeight: '1em',
							letterSpacing: '0.00625em',
							color: '#000000'
						}}
					>
						Information
					</Typography>
				</Box>

				{/* All Info Content */}
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '4px', width: '100%' }}>
					{/* Call us Block */}
					{merchant?.phone && (
						<>
							<InfoBlockRow
								label="Call us"
								icon={<PhoneOutlinedIcon sx={{ width: '20px', height: '20px', color: '#4A5568' }} />}
							>
								<Typography
									component="a"
									href={`tel:${merchant.phone}`}
									sx={{
										fontFamily: 'Inter, sans-serif',
										fontWeight: 400,
										fontSize: '14px',
										lineHeight: '1.71em',
										color: '#2D3748',
										textDecoration: 'none',
										'&:hover': { textDecoration: 'underline' }
									}}
								>
									{formatPhoneForDisplay(merchant.phone)}
								</Typography>
							</InfoBlockRow>
							{/* Separator */}
							<Box sx={{ width: '100%', height: '0px', borderBottom: '1px solid #CBD5E0' }} />
						</>
					)}

					{/* Hours Block */}
					{weeklyHours && (
						<>
							<Box
								onClick={() => setHoursPopupOpen(true)}
								sx={{ cursor: 'pointer' }}
							>
								<InfoBlockRow
									label="Hours"
									icon={<ScheduleOutlinedIcon sx={{ width: '18px', height: '18px', color: '#4A5568' }} />}
								>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
										<Typography
											component="span"
											sx={{
												fontFamily: 'Inter, sans-serif',
												fontWeight: 400,
												fontSize: '14px',
												lineHeight: '1.71em',
												color: openStatus?.isOpen ? '#17D841' : '#E03535'
											}}
										>
											{openStatus?.isOpen ? 'Open' : 'Closed'}
										</Typography>
										<Typography
											component="span"
											sx={{
												fontFamily: 'Inter, sans-serif',
												fontWeight: 400,
												fontSize: '14px',
												lineHeight: '1.71em',
												color: '#2D3748'
											}}
										>
											{getHoursDisplayText()}
										</Typography>
									</Box>
								</InfoBlockRow>
							</Box>
							{/* Separator */}
							<Box sx={{ width: '100%', height: '0px', borderBottom: '1px solid #CBD5E0' }} />
						</>
					)}

					{/* Website Block */}
					{merchant?.website && (
						<>
							<Box
								onClick={() => window.open(merchant.website.startsWith('http') ? merchant.website : `https://${merchant.website}`, '_blank')}
								sx={{ cursor: 'pointer' }}
							>
								<InfoBlockRow
									label="Website"
									icon={<OpenInNewOutlinedIcon sx={{ width: '20px', height: '20px', color: '#4A5568' }} />}
								>
									<Typography
										sx={{
											fontFamily: 'Inter, sans-serif',
											fontWeight: 400,
											fontSize: '14px',
											lineHeight: '1.71em',
											color: '#2D3748',
											'&:hover': { textDecoration: 'underline' }
										}}
									>
										{merchant.website.replace(/^https?:\/\//, '')}
									</Typography>
								</InfoBlockRow>
							</Box>
							{/* Separator */}
							<Box sx={{ width: '100%', height: '0px', borderBottom: '1px solid #CBD5E0' }} />
						</>
					)}

					{/* Location Block */}
					{hasAddress(merchant) && (
						<InfoBlockRow
							label="Location"
							icon={<LocationOnOutlinedIcon sx={{ width: '20px', height: '20px', color: '#4A5568' }} />}
						>
							<Typography
								sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 400,
									fontSize: '14px',
									lineHeight: '1.71em',
									color: '#2D3748'
								}}
							>
								{getDisplayAddress(merchant)}
							</Typography>
						</InfoBlockRow>
					)}

					{/* Google Map */}
					{hasAddress(merchant) && (
						<StoreLocationMap address={getDisplayAddress(merchant)} />
					)}
				</Box>
			</Box>

			{/* Hours Popup - Lazy loaded */}
			<Suspense fallback={null}>
				<StoreHoursPopup
					open={hoursPopupOpen}
					onClose={() => setHoursPopupOpen(false)}
					weeklyHours={weeklyHours}
					timezone={merchant?.timezone}
				/>
			</Suspense>
		</Box>
	)
}

// Map for store location using Google Maps Embed API (free, no billing needed)
function StoreLocationMap({ address }) {
	const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
	if (!address || !apiKey) return null

	const encodedAddress = encodeURIComponent(address)
	const mapSrc = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodedAddress}&zoom=15`
	const directionsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`

	return (
		<Box
			sx={{
				width: '100%',
				height: '200px',
				borderRadius: '12px',
				overflow: 'hidden',
				position: 'relative',
				cursor: 'pointer'
			}}
			onClick={() => window.open(directionsUrl, '_blank')}
		>
			<iframe
				src={mapSrc}
				title="Store Location"
				style={{
					width: '100%',
					height: '100%',
					border: 'none',
					pointerEvents: 'none'
				}}
				loading="lazy"
				referrerPolicy="no-referrer-when-downgrade"
			/>
		</Box>
	)
}

// Main StorePage Component
export default function StorePage() {
	const { slug } = useParams()
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	// Hide scrollbar when rendered inside an iframe (live preview)
	const isInIframe = window !== window.parent
	useEffect(() => {
		if (isInIframe) {
			document.documentElement.style.scrollbarWidth = 'none'
			document.documentElement.style.overflow = 'auto'
			const style = document.createElement('style')
			style.textContent = '::-webkit-scrollbar { display: none !important; }'
			document.head.appendChild(style)
			return () => style.remove()
		}
	}, [isInIframe])

	const [loading, setLoading] = useState(true)
	const [merchant, setMerchant] = useState(null)
	const [services, setServices] = useState([])
	const [galleryPhotos, setGalleryPhotos] = useState([])
	const [logo, setLogo] = useState(null)
	const [errorMessage, setErrorMessage] = useState('')
	const [mobileActiveTab, setMobileActiveTab] = useState('about') // 'about' | 'services' | 'info'
	const [mobileHoursPopupOpen, setMobileHoursPopupOpen] = useState(false)
	const [showStickyHeader, setShowStickyHeader] = useState(false)
	const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false)
	const photoGalleryRef = useRef(null)

	// Mobile section refs for scroll navigation
	const aboutSectionRef = useRef(null)
	const servicesSectionRef = useRef(null)
	const infoSectionRef = useRef(null)

	// Handle mobile tab click - scroll to section
	const isTabScrollingRef = useRef(false)

	const handleMobileTabChange = useCallback((tabId) => {
		setMobileActiveTab(tabId)
		isTabScrollingRef.current = true

		let targetRef = null
		if (tabId === 'about') targetRef = aboutSectionRef
		else if (tabId === 'services') targetRef = servicesSectionRef
		else if (tabId === 'info') targetRef = infoSectionRef

		if (targetRef?.current) {
			setTimeout(() => {
				targetRef.current.scrollIntoView({
					behavior: 'smooth',
					block: 'start'
				})
				setTimeout(() => {
					isTabScrollingRef.current = false
				}, 600)
			}, 10)
		} else {
			isTabScrollingRef.current = false
		}
	}, [])

	// Track which section is active on mobile using top edge detection
	useEffect(() => {
		if (!isMobile || loading) return

		const TAB_BAR_HEIGHT = 52

		// Order matches content: about, info, services
		const sections = [
			{ id: 'about', ref: aboutSectionRef },
			{ id: 'info', ref: infoSectionRef },
			{ id: 'services', ref: servicesSectionRef }
		]

		const handleScroll = () => {
			if (isTabScrollingRef.current) return

			let activeId = 'about'
			for (const section of sections) {
				if (section.ref.current) {
					const rect = section.ref.current.getBoundingClientRect()
					if (rect.top <= TAB_BAR_HEIGHT + 10) {
						activeId = section.id
					}
				}
			}
			setMobileActiveTab(activeId)
		}

		window.addEventListener('scroll', handleScroll, { passive: true })
		document.addEventListener('scroll', handleScroll, { passive: true, capture: true })
		handleScroll()

		return () => {
			window.removeEventListener('scroll', handleScroll)
			document.removeEventListener('scroll', handleScroll, { capture: true })
		}
	}, [isMobile, loading])

	// Track scroll to show/hide sticky header on desktop using IntersectionObserver
	useEffect(() => {
		if (isMobile || loading) return

		let observer = null

		// Small delay to ensure ref is attached after render
		const timeoutId = setTimeout(() => {
			const element = photoGalleryRef.current
			if (!element) return

			observer = new IntersectionObserver(
				(entries) => {
					const entry = entries[0]
					// Show sticky header when the gallery is not intersecting (scrolled past)
					setShowStickyHeader(!entry.isIntersecting)
				},
				{
					threshold: 0,
					rootMargin: '0px 0px 0px 0px'
				}
			)

			observer.observe(element)
		}, 100)

		return () => {
			clearTimeout(timeoutId)
			if (observer) {
				observer.disconnect()
			}
		}
	}, [isMobile, loading])

	// Load merchant data
	const loadData = useCallback(async () => {
		setLoading(true)
		setErrorMessage('')

		try {
			// Load merchant
			const { data: merchantData, error: merchantError } = await supabase
				.from('merchant')
				.select('id, name, slug, address, address_2, city, state, zip, phone, email, website, description, timezone, weekly_hours, weekly_hours_display, inherited_hours, closed_recurring_dates, active, is_landing_page_active, currency, social_links')
				.eq('slug', slug)
				.maybeSingle()

			if (merchantError) throw merchantError

			// Check if landing page is active
			if (merchantData && merchantData.is_landing_page_active === false) {
				setMerchant(null)
				return
			}

			if (!merchantData) {
				setMerchant(null)
				return
			}

			setMerchant(merchantData)

			// Load services with categories
			const { data: servicesData, error: servicesError } = await supabase
				.from('service_types')
				.select(`
					id,
					name,
					duration_minutes,
					category_id,
					price,
					description,
					is_service,
					is_add_on,
					categories (
						id,
						name,
						order
					)
				`)
				.eq('merchant_id', merchantData.id)
				.order('name')

			if (servicesError) {
				console.error('Error loading services:', servicesError)
			} else {
				setServices(servicesData || [])
			}

			// Load gallery photos (all photos, sorted by order)
			const galleryPhotosData = await fetchMerchantGalleryPhotos(merchantData.id)
			// Filter to get photos with order >= 1 (PhotoGallery will display first 4)
			const filteredPhotos = galleryPhotosData.filter(p => p.order >= 1)
			setGalleryPhotos(filteredPhotos)

			// Load logo
			const logoData = await fetchMerchantLogo(merchantData.id)
			setLogo(logoData)

		} catch (e) {
			console.error('Error loading data:', e)
			setErrorMessage(e.message || 'Failed to load store')
		} finally {
			setLoading(false)
		}
	}, [slug])

	useEffect(() => {
		loadData()
	}, [loadData])

	// Loading state
	if (loading) {
		return (
			<Box
				sx={{
					minHeight: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					background: THEME_COLORS.gradient
				}}
			>
				<CircularProgress sx={{ color: THEME_COLORS.primary }} />
			</Box>
		)
	}

	// Not found state
	if (errorMessage || !merchant) {
		return (
			<Box
				sx={{
					minHeight: '100vh',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					background: THEME_COLORS.gradient,
					p: 4
				}}
			>
				<Box
					sx={{
						bgcolor: '#FFFFFF',
						borderRadius: '16px',
						p: 4,
						maxWidth: '400px',
						textAlign: 'center'
					}}
				>
					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: '24px',
							color: '#2D3748',
							mb: 2
						}}
					>
						Store not found
					</Typography>
					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: '16px',
							color: '#718096'
						}}
					>
						We couldn't find a store for "{slug}".
					</Typography>
					{errorMessage && (
						<Typography sx={{ color: 'error.main', mt: 2, fontSize: '14px' }}>
							{errorMessage}
						</Typography>
					)}
				</Box>
			</Box>
		)
	}

	// Generate SEO metadata
	const seoTitle = `${merchant.name} | Book Pet Services Online`
	const seoDescription = merchant.description
		? merchant.description.substring(0, 160)
		: `Book pet grooming and services at ${merchant.name}. View services, hours, and book your appointment online.`
	const logoUrl = logo ? getMerchantPhotoUrl(logo) : null
	const pageUrl = `https://nami.pet/store/${merchant.slug}`

	return (
		<>
			{/* SEO Meta Tags - React 19 hoists these to <head> */}
			<title>{seoTitle}</title>
			<meta name="description" content={seoDescription} />
			<meta name="robots" content="index, follow" />
			<link rel="canonical" href={pageUrl} />

			{/* Open Graph / Facebook */}
			<meta property="og:type" content="business.business" />
			<meta property="og:url" content={pageUrl} />
			<meta property="og:title" content={seoTitle} />
			<meta property="og:description" content={seoDescription} />
			{logoUrl && <meta property="og:image" content={logoUrl} />}
			<meta property="og:site_name" content="Nami Pet Services" />

			{/* Twitter */}
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:url" content={pageUrl} />
			<meta name="twitter:title" content={seoTitle} />
			<meta name="twitter:description" content={seoDescription} />
			{logoUrl && <meta name="twitter:image" content={logoUrl} />}

			{/* Preload critical resources */}
			{logoUrl && <link rel="preload" as="image" href={logoUrl} />}
			{galleryPhotos[0] && (
				<link rel="preload" as="image" href={getMerchantPhotoUrl(galleryPhotos[0])} />
			)}

			<Box
				sx={{
					minHeight: '100vh',
					bgcolor: isMobile ? '#FFFFFF' : '#F7F6F3',
					position: 'relative',
					overflow: 'visible', // Must be visible for sticky to work
					pt: isMobile ? 0 : 4,
					pb: isMobile ? '120px' : 4,
					px: isMobile ? 0 : 4
				}}
			>
				{/* Background gradient orbs container - desktop only */}
				{/* Uses fixed positioning to contain orbs without affecting sticky behavior */}
				{!isMobile && (
					<Box
						sx={{
							position: 'fixed',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							overflow: 'hidden',
							pointerEvents: 'none',
							zIndex: 0
						}}
					>
						{/* Purple gradient orb - top left */}
						<Box
							sx={{
								position: 'absolute',
								top: '-400px',
								left: '-300px',
								width: '1200px',
								height: '1200px',
								background: 'linear-gradient(180deg, rgba(233, 216, 253, 0.60) 0%, rgba(159, 122, 234, 0.60) 100%)',
								filter: 'blur(300px)',
								WebkitFilter: 'blur(300px)',
								transform: 'translate3d(0, 0, 0)',
								WebkitTransform: 'translate3d(0, 0, 0)',
								WebkitBackfaceVisibility: 'hidden',
								backfaceVisibility: 'hidden'
							}}
						/>
						{/* Pink orb - right side */}
						<Box
							sx={{
								position: 'absolute',
								top: '200px',
								right: '-200px',
								width: '800px',
								height: '800px',
								bgcolor: '#FFD2D2',
								borderRadius: '50%',
								filter: 'blur(200px)',
								WebkitFilter: 'blur(200px)',
								transform: 'translate3d(0, 0, 0)',
								WebkitTransform: 'translate3d(0, 0, 0)',
								WebkitBackfaceVisibility: 'hidden',
								backfaceVisibility: 'hidden'
							}}
						/>
						{/* Blue orb - bottom left */}
						<Box
							sx={{
								position: 'absolute',
								top: '600px',
								left: '-300px',
								width: '800px',
								height: '800px',
								bgcolor: '#BEDBFF',
								borderRadius: '50%',
								filter: 'blur(300px)',
								WebkitFilter: 'blur(300px)',
								transform: 'translate3d(0, 0, 0)',
								WebkitTransform: 'translate3d(0, 0, 0)',
								WebkitBackfaceVisibility: 'hidden',
								backfaceVisibility: 'hidden'
							}}
						/>
					</Box>
				)}

				<Box
					sx={{
						maxWidth: '1600px',
						margin: '0 auto',
						position: 'relative',
						zIndex: 1,
						bgcolor: isMobile ? 'transparent' : '#FFFFFF',
						borderTopLeftRadius: isMobile ? 0 : '12px',
						borderTopRightRadius: isMobile ? 0 : '12px',
						borderBottomLeftRadius: isMobile ? 0 : '12px',
						borderBottomRightRadius: isMobile ? 0 : '12px',
						pb: isMobile ? 0 : '20px',
						boxShadow: isMobile ? 'none' : '0px 4px 4px -4px rgba(12, 12, 13, 0.05), 0px 16px 32px -4px rgba(12, 12, 13, 0.1)',
						overflow: isMobile ? 'visible' : 'visible' // Ensure sticky works
					}}
				>
					{/* Photo Gallery with Store Name Overlay */}
					<Box
						ref={photoGalleryRef}
						sx={{
							position: 'relative',
							borderTopLeftRadius: isMobile ? 0 : '12px',
							borderTopRightRadius: isMobile ? 0 : '12px',
							overflow: isMobile ? 'visible' : 'hidden' // Only clip on desktop for border-radius
						}}
					>
						<PhotoGallery
							photos={galleryPhotos}
							merchant={merchant}
							logo={logo}
							isMobile={isMobile}
							onSeeHoursClick={() => setMobileHoursPopupOpen(true)}
							onSeeAllPhotos={() => setPhotoGalleryOpen(true)}
						/>
					</Box>

					{/* Mobile: Sticky Tab Selector - Section Jump Navigation */}
					{isMobile && (
						<MobileTabSelector
							activeTab={mobileActiveTab}
							onTabChange={handleMobileTabChange}
						/>
					)}

					{/* Main Content */}
					<Box
						sx={{
							display: 'flex',
							flexDirection: isMobile ? 'column' : 'row',
							alignItems: isMobile ? 'stretch' : 'flex-start', // flex-start needed for sticky to work
							gap: isMobile ? 0 : 4,
							p: isMobile ? 0 : '20px',
							pt: isMobile ? 0 : '30px'
						}}
					>
						{/* Mobile: Single scroll with all sections */}
						{isMobile && (
							<Box sx={{ width: '100%', pt: "20px", gap: '34px', display: 'flex', flexDirection: 'column' }}>
								{/* About Section + Social Links */}
								<Box ref={aboutSectionRef} sx={{ scrollMarginTop: '48px' }}>
									<AboutSection merchant={merchant} isMobile={isMobile} />
									{/* Social media links - mobile only */}
									{merchant?.social_links && (merchant.social_links.instagram || merchant.social_links.google || merchant.social_links.yelp || merchant.social_links.facebook) && (
										<Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '5px', px: '16px' }}>
											{merchant.social_links.instagram && (
												<Box
													component="a"
													href={merchant.social_links.instagram.startsWith('http') ? merchant.social_links.instagram : `https://${merchant.social_links.instagram}`}
													target="_blank"
													rel="noopener noreferrer"
													sx={{ display: 'flex', textDecoration: 'none' }}
												>
													<Box component="img" src={InstagramIconSvg} alt="Instagram" sx={{ width: '29px', height: '29px' }} />
												</Box>
											)}
											{merchant.social_links.facebook && (
												<Box
													component="a"
													href={merchant.social_links.facebook.startsWith('http') ? merchant.social_links.facebook : `https://${merchant.social_links.facebook}`}
													target="_blank"
													rel="noopener noreferrer"
													sx={{ display: 'flex', textDecoration: 'none' }}
												>
													<Box component="img" src={FacebookIconSvg} alt="Facebook" sx={{ width: '24px', height: '24px' }} />
												</Box>
											)}
											{merchant.social_links.yelp && (
												<Box
													component="a"
													href={merchant.social_links.yelp.startsWith('http') ? merchant.social_links.yelp : `https://${merchant.social_links.yelp}`}
													target="_blank"
													rel="noopener noreferrer"
													sx={{ display: 'flex', textDecoration: 'none' }}
												>
													<Box component="img" src={YelpIconSvg} alt="Yelp" sx={{ width: '29px', height: '29px' }} />
												</Box>
											)}
											{merchant.social_links.google && (
												<Box
													component="a"
													href={merchant.social_links.google.startsWith('http') ? merchant.social_links.google : `https://${merchant.social_links.google}`}
													target="_blank"
													rel="noopener noreferrer"
													sx={{ display: 'flex', textDecoration: 'none' }}
												>
													<Box component="img" src={GoogleIconSvg} alt="Google" sx={{ width: '24px', height: '24px' }} />
												</Box>
											)}
										</Box>
									)}
								</Box>

								{/* Info Section */}
								<Box ref={infoSectionRef} sx={{ scrollMarginTop: '48px', pt: 2 }}>
									<StoreInfo
										merchant={merchant}
										isMobile={isMobile}
									/>
								</Box>

								{/* Services Section - at bottom */}
								<Box ref={servicesSectionRef} sx={{ scrollMarginTop: '48px' }}>
									<ServicesPreview
										services={services}
										merchant={merchant}
										isMobile={isMobile}
									/>
								</Box>
							</Box>
						)}

						{/* Desktop: Services on left */}
						{!isMobile && (
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<AboutSection merchant={merchant} isMobile={isMobile} />
								<ServicesPreview
									services={services}
									merchant={merchant}
									isMobile={isMobile}
								/>
							</Box>
						)}

						{/* Desktop: Store Info on right - sticky sidebar */}
						{!isMobile && (
							<Box
								sx={{
									width: '405px',
									flexShrink: 0,
									alignSelf: 'flex-start', // Required for sticky to work in flex container
									position: 'sticky',
									top: '24px'
								}}
							>
								<StoreInfo
									merchant={merchant}
									isMobile={isMobile}
									showStickyHeader={showStickyHeader}
									logo={logo}
									socialLinks={merchant?.social_links}
								/>
							</Box>
						)}
					</Box>
				</Box>

				{/* Mobile: Floating Book Now Button */}
				{isMobile && merchant?.active && (
					<Box
						sx={{
							position: 'fixed',
							bottom: 0,
							left: 0,
							right: 0,
							p: '16px 32px',
							pb: '32px',
							zIndex: 1000
						}}
					>
						<Box
							component={Link}
							to={`/store/${merchant?.slug}/book`}
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								width: '100%',
								height: '56px',
								bgcolor: THEME_COLORS.accent,
								border: `1px solid ${THEME_COLORS.accent}`,
								borderRadius: '9999px',
								textDecoration: 'none',
								fontFamily: 'Inter, sans-serif',
								fontWeight: 600,
								fontSize: '16px',
								lineHeight: '1.21em',
								letterSpacing: '-0.016em',
								color: '#FFFFFF',
								'&:hover': {
									bgcolor: THEME_COLORS.accentHover
								}
							}}
						>
							Book Now
						</Box>
					</Box>
				)}

				{/* Mobile: Hours Popup */}
				{isMobile && (
					<Suspense fallback={null}>
						<StoreHoursPopup
							open={mobileHoursPopupOpen}
							onClose={() => setMobileHoursPopupOpen(false)}
							weeklyHours={getDisplayWeeklyHours(merchant)}
							timezone={merchant?.timezone}
						/>
					</Suspense>
				)}

				{/* Photo Gallery Modal */}
				<Suspense fallback={null}>
					<PhotoGalleryModal
						open={photoGalleryOpen}
						onClose={() => setPhotoGalleryOpen(false)}
						photos={galleryPhotos}
						merchantName={merchant?.name}
					/>
				</Suspense>
			</Box>
		</>
	)
}
